import type { WorkflowSharedTypes } from '@openphone/dexie-database/types'
import { makeAutoObservable, observable } from 'mobx'

import type Service from '.'
import PersistedCollection from './collections/PersistedCollection'
import type { RawWorkflowDefinitionCriteriaType } from './model/workflow/WorkflowDefinitionCriteriaTypeModel'
import WorkflowDefinitionCriteriaTypeModel from './model/workflow/WorkflowDefinitionCriteriaTypeModel'
import { isBranchingEnabled } from './utils/isBranchingEnabled'
import type { WorkflowDefinitionCriteriaTypeRepository } from './worker/repository/WorkflowDefinitionCriteriaTypeRepository'
import { WORKFLOW_DEFINITION_CRITERIA_TYPE_TABLE_NAME } from './worker/repository/WorkflowDefinitionCriteriaTypeRepository'

export default class WorkflowDefinitionCriteriaTypeStore {
  private readonly collection: PersistedCollection<
    WorkflowDefinitionCriteriaTypeModel,
    WorkflowDefinitionCriteriaTypeRepository
  >
  private fetched = false

  constructor(private root: Service) {
    this.collection = new PersistedCollection({
      table: root.storage.table(WORKFLOW_DEFINITION_CRITERIA_TYPE_TABLE_NAME),
      classConstructor: (json: RawWorkflowDefinitionCriteriaType) =>
        new WorkflowDefinitionCriteriaTypeModel(json),
    })

    makeAutoObservable<this, 'collection'>(this, {
      collection: observable,
    })

    if (isBranchingEnabled(this.root.flags.flags)) {
      void this.load()
    }
  }

  get conditionIds(): WorkflowSharedTypes.ConditionDefinitionId[] {
    return this.collection.list.map((group) => group.conditionId)
  }

  getCriteriaTypeGroup(
    conditionId: WorkflowSharedTypes.ConditionDefinitionId,
  ): WorkflowDefinitionCriteriaTypeModel | null {
    // `skipStorage`: `load()` already reads the whole table, so a miss here means "not
    // loaded yet", not something worth a lazy IndexedDB read.
    return this.collection.get(conditionId, { skipStorage: true }) ?? null
  }

  fetchCriteriaTypes() {
    if (this.fetched || !isBranchingEnabled(this.root.flags.flags)) {
      return
    }

    this.fetched = true

    void this.root.transport.workflow.criteriaTypes
      .list(1000)
      .then((response) => {
        const models = response.data.map(
          (item) => new WorkflowDefinitionCriteriaTypeModel(item),
        )
        const fetchedIds = new Set(models.map((model) => model.id))

        // The response is the whole catalog, so a cached entry missing from it is stale
        this.collection.deleteBulk(
          this.collection.list.filter((model) => !fetchedIds.has(model.id)),
        )
        this.collection.putBulk(models)
      })
      .catch(() => {
        this.fetched = false
      })
  }

  private load() {
    return this.collection.performQuery((repo) => repo.all())
  }
}
